import React, { useEffect, useMemo, useState } from "react";
import { NavLink, useLocation } from "react-router-dom";
import AdminSidebar from "../../sidebar/admin_sidebar";
import AdminHeader from "../../header/admin_header";
import { apiCall } from "../../../_services/apiCall";
import config from "../../../config/config.json";

// ── unchanged data ────────────────────────────────────────────────────────────
const companyOptions = [
  { label: "Manipal", value: "351" },
  { label: "Kennametal", value: "362" },
  { label: "Sify", value: "331" },
];
const TEMPLATE_PREFERENCE_API = `${config.ADMIN_API_URL}company-template-permission`;

const getCompanyLabel = (id) =>
  companyOptions.find((o) => o.value === String(id))?.label || String(id);
const getUsageLabel = (templateType) =>
  String(templateType) === "custom_template" ? "Enabled" : "Disabled";
const getTemplateAsset = (companyId) => {
  if (String(companyId) === "331") {
    return { fileName: "Sify_Standard_Template.xlsx", url: "/Sify_Standard_Template.xlsx" };
  }
  if (String(companyId) === "351") {
    return { fileName: "Standard_Manipal_Template.xlsx", url: "/Standard_Manipal_Template.xlsx" };
  }
  return null;
};
const mapPermissionRow = (item) => {
  const companyId    = item?.companyId   ?? item?.company_id;
  const templateType = item?.templateType ?? item?.template_type;
  const isEnabled    = item?.isEnabled ?? item?.is_enabled;
  const asset        = getTemplateAsset(companyId);
  return {
    id: companyId, companyId,
    company:              getCompanyLabel(companyId),
    templateType,
    isEnabled:            Boolean(Number(isEnabled)),
    usageLabel:           Number(isEnabled) ? "Enabled" : "Disabled",
    templateDownloadUrl:  asset?.url || "",
    templateFileName:     asset?.fileName || "",
  };
};

// ── company initials avatar ───────────────────────────────────────────────────
const Avatar = ({ name }) => {
  const initials = name?.split(" ").map((w) => w[0]).join("").slice(0, 2).toUpperCase();
  const colors   = ["#3f88a5", "#2e6da4", "#1a7f64", "#7c3aed", "#b45309"];
  const color    = colors[name?.charCodeAt(0) % colors.length] || "#3f88a5";
  return (
    <span style={{
      width: 32, height: 32, borderRadius: 8, background: color,
      display: "inline-flex", alignItems: "center", justifyContent: "center",
      fontSize: 11, fontWeight: 700, color: "#fff", flexShrink: 0, letterSpacing: "0.5px",
    }}>{initials}</span>
  );
};

// ── badge ─────────────────────────────────────────────────────────────────────
const Badge = ({ label, type }) => {
  const styles = {
    global_template: { bg: "#fff7ed", color: "#c2410c", border: "#fed7aa" },
    custom_template: { bg: "#f0fdf4", color: "#166534", border: "#bbf7d0" },
  };
  const s = styles[type] || { bg: "#f1f5f9", color: "#475569", border: "#e2e8f0" };
  return (
    <span style={{
      fontSize: 11, fontWeight: 600, padding: "3px 9px", borderRadius: 20,
      background: s.bg, color: s.color, border: `1px solid ${s.border}`,
      whiteSpace: "nowrap",
    }}>{label}</span>
  );
};

// ── styled select ─────────────────────────────────────────────────────────────
const StyledSelect = ({ value, onChange, placeholder, options, disabled }) => (
  <div style={{ position: "relative" }}>
    <select
      value={value}
      onChange={onChange}
      disabled={disabled}
      style={{
        width: "100%",
        padding: "10px 36px 10px 14px",
        fontSize: 13,
        fontFamily: "inherit",
        color: value ? "#0f172a" : "#94a3b8",
        background: disabled ? "#f8fafc" : "#fff",
        border: "1.5px solid #e2e8f0",
        borderRadius: 10,
        outline: "none",
        appearance: "none",
        WebkitAppearance: "none",
        cursor: disabled ? "not-allowed" : "pointer",
        transition: "border-color 0.15s",
      }}
      onFocus={(e) => (e.target.style.borderColor = "#3f88a5")}
      onBlur={(e)  => (e.target.style.borderColor = "#e2e8f0")}
    >
      <option value="">{placeholder}</option>
      {options.map((o) => (
        <option key={o.value} value={o.value}>{o.label}</option>
      ))}
    </select>
    <span style={{
      position: "absolute", right: 12, top: "50%", transform: "translateY(-50%)",
      pointerEvents: "none", fontSize: 11, color: "#94a3b8",
    }}>▾</span>
  </div>
);

// ── main component ────────────────────────────────────────────────────────────
const ToggleSwitch = ({ checked, onChange, disabled }) => (
  <button
    type="button"
    onClick={() => !disabled && onChange(!checked)}
    disabled={disabled}
    style={{
      width: 56,
      height: 30,
      borderRadius: 999,
      border: "none",
      padding: 3,
      background: checked ? "#16a34a" : "#cbd5e1",
      cursor: disabled ? "not-allowed" : "pointer",
      transition: "background 0.2s ease",
      position: "relative",
      opacity: disabled ? 0.6 : 1,
    }}
    aria-pressed={checked}
    aria-label={checked ? "Disable template usage" : "Enable template usage"}
  >
    <span
      style={{
        width: 24,
        height: 24,
        borderRadius: "50%",
        background: "#fff",
        display: "block",
        transform: checked ? "translateX(26px)" : "translateX(0)",
        transition: "transform 0.2s ease",
        boxShadow: "0 1px 4px rgba(0,0,0,0.2)",
      }}
    />
  </button>
);

const Permission = () => {
  const location = useLocation();
  const [selectedCompany,  setSelectedCompany]  = useState("");
  const [selectedUsage, setSelectedUsage] = useState(false);
  const [savedSelections,  setSavedSelections]  = useState([]);
  const [saving,           setSaving]           = useState(false);
  const [updatingId,       setUpdatingId]        = useState(null);

  const selectedTemplateAsset = useMemo(() => (selectedCompany ? getTemplateAsset(selectedCompany) : null), [selectedCompany]);
  const canSave = useMemo(
    () => Boolean(selectedCompany && selectedTemplateAsset),
    [selectedCompany, selectedTemplateAsset]
  );

  useEffect(() => {
    (async () => {
      const { isSuccess, data } = await apiCall(TEMPLATE_PREFERENCE_API, {}, {}, "GET");
      if (isSuccess) setSavedSelections(Object.values(data?.data || {}).map(mapPermissionRow));
    })();
  }, []);

  const handleCompanyChange  = (e) => {
    const companyId = e.target.value;
    setSelectedCompany(companyId);
    const existingRow = savedSelections.find((item) => String(item.companyId) === String(companyId));
    setSelectedUsage(existingRow ? Boolean(existingRow.isEnabled) : false);
  };

  const handleSave = async () => {
    if (!canSave) return;
    setSaving(true);
      const templateType = "custom_template";
    const { isSuccess, data } = await apiCall(
      TEMPLATE_PREFERENCE_API,
      {},
      { companyId: selectedCompany, templateType, isEnabled: selectedUsage },
      "POST"
    );
    if (isSuccess) {
      const saved = mapPermissionRow(data?.data || {});
      setSavedSelections((prev) => {
        const next = prev.filter((i) => String(i.companyId) !== String(saved.companyId));
        return [...next, saved];
      });
      setSelectedCompany("");
      setSelectedUsage(false);
    }
    setSaving(false);
  };

  const handleToggleUsage = async (item) => {
    setUpdatingId(item.id);
    const nextEnabled = !item.isEnabled;
    const { isSuccess, data } = await apiCall(
      TEMPLATE_PREFERENCE_API,
      {},
      {
        companyId: item.companyId,
        templateType: item.templateType || "custom_template",
        isEnabled: nextEnabled,
      },
      "POST"
    );
    if (isSuccess) {
      const saved = mapPermissionRow(data?.data || {});
      setSavedSelections((prev) =>
        prev.map((row) => (String(row.id) === String(saved.id) ? saved : row))
      );
    }
    setUpdatingId(null);
  };

  return (
    <div>
      <AdminSidebar dataFromParent={location.pathname} />
      <AdminHeader />

      <div className="main_wrapper">
        <div className="inner_wraapper">
          <div className="hol_rell">

            {/* ── tab strip ── */}
            <div className="steps-form">
              <div className="steps-row setup-panel">
                <div className="tabs-top setting_admin my-0">
                  <ul>
                    <li><NavLink to="/notification" title="Notification">Notification</NavLink></li>
                    <li><NavLink to="#" className="activee" title="Permission">Permission</NavLink></li>
                  </ul>
                </div>
              </div>
            </div>

            {/* ── page body ── */}
            <div className="color_div_on framwork_2 pt-0">
              <div className="business_detail">
                <div style={{ padding: "28px 0", maxWidth: 860, margin: "0 auto" }}>

                  {/* page title */}
                  <div style={{ marginBottom: 28 }}>
                    <h4 style={{ fontSize: 20, fontWeight: 700, color: "#0f172a", margin: 0 }}>
                      Permission Settings
                    </h4>
                    <p style={{ fontSize: 13, color: "#64748b", margin: "4px 0 0" }}>
                      Assign template access per company. Changes take effect immediately.
                    </p>
                  </div>

                  {/* ── two-column layout ── */}
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 20, marginBottom: 32 }}>

                    {/* left card: form */}
                    <div style={{
                      background: "#fff", border: "1.5px solid #e2e8f0", borderRadius: 14,
                      padding: "24px", display: "flex", flexDirection: "column", gap: 18,
                      boxShadow: "0 1px 4px rgba(0,0,0,0.05)",
                    }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 4 }}>
                        <div style={{ width: 36, height: 36, borderRadius: 10, background: "#eff6ff", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 17 }}>🏢</div>
                          <div>
                            <div style={{ fontSize: 14, fontWeight: 700, color: "#0f172a" }}>Assign Permission</div>
                          <div style={{ fontSize: 11, color: "#94a3b8" }}>Select company and enable or disable template usage</div>
                        </div>
                      </div>

                      <div>
                        <label style={{ fontSize: 11, fontWeight: 700, color: "#475569", textTransform: "uppercase", letterSpacing: "0.05em", display: "block", marginBottom: 7 }}>
                          Company
                        </label>
                        <StyledSelect
                          value={selectedCompany}
                          onChange={handleCompanyChange}
                          placeholder="Select company…"
                          options={companyOptions}
                        />
                      </div>

                      <div>
                        <label style={{ fontSize: 11, fontWeight: 700, color: "#475569", textTransform: "uppercase", letterSpacing: "0.05em", display: "block", marginBottom: 7 }}>
                          Template Usage
                        </label>
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            padding: "12px 14px",
                            border: "1.5px solid #e2e8f0",
                            borderRadius: 10,
                            background: selectedCompany ? "#fff" : "#f8fafc",
                          }}
                        >
                          <div>
                            <div style={{ fontSize: 13, fontWeight: 600, color: "#0f172a" }}>
                              {selectedUsage ? "Enabled" : "Disabled"}
                            </div>
                            <div style={{ fontSize: 11, color: "#94a3b8", marginTop: 2 }}>
                              {selectedUsage
                                ? "Company admin can use this standard template"
                                : selectedTemplateAsset
                                  ? "Standard template stays visible, but usage is disabled"
                                  : "No template is defined for this company"}
                            </div>
                          </div>
                          <ToggleSwitch
                            checked={selectedUsage}
                            onChange={setSelectedUsage}
                            disabled={!selectedCompany || !selectedTemplateAsset}
                          />
                        </div>
                      </div>

                      <button
                        onClick={handleSave}
                        disabled={!canSave || saving}
                        style={{
                          padding: "11px 0", borderRadius: 10, border: "none",
                          background: canSave ? "#3f88a5" : "#e2e8f0",
                          color: canSave ? "#fff" : "#94a3b8",
                          fontSize: 13, fontWeight: 700, cursor: canSave ? "pointer" : "not-allowed",
                          display: "flex", alignItems: "center", justifyContent: "center", gap: 8,
                          transition: "all 0.15s",
                          boxShadow: canSave ? "0 2px 8px rgba(63,136,165,0.35)" : "none",
                        }}
                      >
                        {saving ? (
                          <>
                            <span style={{ width: 13, height: 13, borderRadius: "50%", border: "2px solid rgba(255,255,255,0.3)", borderTopColor: "#fff", animation: "perm-spin 0.7s linear infinite", display: "inline-block" }} />
                            Saving…
                          </>
                        ) : "Save Permission"}
                      </button>
                    </div>

                    {/* right card: download preview */}
                    <div style={{
                      background: "#fff", border: "1.5px solid #e2e8f0", borderRadius: 14,
                      padding: "24px", boxShadow: "0 1px 4px rgba(0,0,0,0.05)",
                      display: "flex", flexDirection: "column", justifyContent: "center",
                    }}>
                      {selectedTemplateAsset ? (
                        <>
                          <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 18 }}>
                            <div style={{ width: 36, height: 36, borderRadius: 10, background: "#f0fdf4", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 18 }}>📄</div>
                            <div>
                              <div style={{ fontSize: 14, fontWeight: 700, color: "#0f172a" }}>Template File</div>
                              <div style={{ fontSize: 11, color: "#94a3b8" }}>
                                {selectedUsage ? "Ready to download" : "Visible even when disabled"}
                              </div>
                            </div>
                          </div>
                          <div style={{ background: "#f8fafc", borderRadius: 10, padding: "14px 16px", border: "1px solid #e2e8f0", marginBottom: 16 }}>
                            <div style={{ fontSize: 11, color: "#64748b", marginBottom: 4 }}>File name</div>
                            <div style={{ fontSize: 13, fontWeight: 600, color: "#0f172a", wordBreak: "break-all" }}>
                              📊 {selectedTemplateAsset.fileName}
                            </div>
                          </div>
                          <a
                            href={selectedTemplateAsset.url}
                            download={selectedTemplateAsset.fileName}
                            style={{
                              display: "flex", alignItems: "center", justifyContent: "center", gap: 8,
                              padding: "11px 0", borderRadius: 10, textDecoration: "none",
                              background: "#f0fdf4", color: "#166534", border: "1.5px solid #bbf7d0",
                              fontSize: 13, fontWeight: 700, transition: "all 0.15s",
                            }}
                          >
                            ↓ Download Template
                          </a>
                        </>
                      ) : (
                        <div style={{ textAlign: "center", padding: "20px 0" }}>
                          <div style={{ fontSize: 40, marginBottom: 12 }}>📋</div>
                          <div style={{ fontSize: 13, fontWeight: 600, color: "#94a3b8" }}>
                            {!selectedCompany
                              ? "Select a company to manage template usage"
                              : "No template defined for this company"}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* ── saved permissions table ── */}
                  <div style={{ background: "#fff", border: "1.5px solid #e2e8f0", borderRadius: 14, overflow: "hidden", boxShadow: "0 1px 4px rgba(0,0,0,0.05)" }}>
                    {/* table header */}
                    <div style={{ padding: "16px 24px", borderBottom: "1px solid #f1f5f9", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                      <div>
                        <div style={{ fontSize: 14, fontWeight: 700, color: "#0f172a" }}>Saved Permissions</div>
                        <div style={{ fontSize: 11, color: "#94a3b8", marginTop: 2 }}>
                          {savedSelections.length} entr{savedSelections.length === 1 ? "y" : "ies"} configured
                        </div>
                      </div>
                      <span style={{ fontSize: 11, fontWeight: 600, padding: "3px 10px", borderRadius: 20, background: "#eff6ff", color: "#3f88a5", border: "1px solid #bfdbfe" }}>
                        {savedSelections.length} total
                      </span>
                    </div>

                    {/* column headers */}
                    <div style={{
                      display: "grid", gridTemplateColumns: "2fr 1.3fr 1.2fr 120px",
                      padding: "10px 24px", background: "#f8fafc",
                      borderBottom: "1px solid #f1f5f9",
                    }}>
                      {["Company", "Usage", "Template", "Action"].map((h, i) => (
                        <span key={i} style={{ fontSize: 10, fontWeight: 700, color: "#94a3b8", textTransform: "uppercase", letterSpacing: "0.05em" }}>{h}</span>
                      ))}
                    </div>

                    {/* rows */}
                    {savedSelections.length === 0 ? (
                      <div style={{ padding: "48px 24px", textAlign: "center" }}>
                        <div style={{ fontSize: 32, marginBottom: 10 }}>🔒</div>
                        <div style={{ fontSize: 13, fontWeight: 600, color: "#94a3b8" }}>No permissions configured yet</div>
                        <div style={{ fontSize: 12, color: "#cbd5e1", marginTop: 4 }}>Use the form above to assign a template to a company</div>
                      </div>
                    ) : (
                      savedSelections.map((item, idx) => (
                        <div
                          key={item.id}
                          style={{
                            display: "grid", gridTemplateColumns: "2fr 1.3fr 1.2fr 120px",
                            padding: "14px 24px", alignItems: "center",
                            borderBottom: idx === savedSelections.length - 1 ? "none" : "1px solid #f8fafc",
                            background: "#fff", transition: "background 0.1s",
                          }}
                          onMouseEnter={(e) => (e.currentTarget.style.background = "#fafbfc")}
                          onMouseLeave={(e) => (e.currentTarget.style.background = "#fff")}
                        >
                          {/* company */}
                          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                            <Avatar name={item.company} />
                            <span style={{ fontSize: 13, fontWeight: 600, color: "#0f172a" }}>{item.company}</span>
                          </div>

                          {/* usage badge */}
                          <div><Badge label={item.usageLabel} type={item.isEnabled ? "custom_template" : "global_template"} /></div>

                          {/* download link */}
                          <div>
                            {item.templateDownloadUrl ? (
                              <a
                                href={item.templateDownloadUrl}
                                download={item.templateFileName}
                                style={{
                                  fontSize: 12, fontWeight: 600, color: "#3f88a5",
                                  textDecoration: "none", display: "inline-flex", alignItems: "center", gap: 4,
                                }}
                              >
                                ↓ Download
                              </a>
                            ) : (
                              <span style={{ fontSize: 12, color: "#94a3b8" }}>No template defined</span>
                            )}
                          </div>

                          {/* reset button */}
                          <div>
                            <button
                              onClick={() => handleToggleUsage(item)}
                              disabled={updatingId === item.id || !item.templateDownloadUrl}
                              style={{
                                padding: "6px 12px", borderRadius: 7, border: "1.5px solid #cbd5e1",
                                background: "#f8fafc", color: "#334155", fontSize: 11, fontWeight: 700,
                                cursor: updatingId === item.id || !item.templateDownloadUrl ? "not-allowed" : "pointer",
                                opacity: updatingId === item.id || !item.templateDownloadUrl ? 0.5 : 1,
                                transition: "all 0.15s",
                              }}
                              onMouseEnter={(e) => {
                                if (updatingId !== item.id && item.templateDownloadUrl) {
                                  e.currentTarget.style.background = "#eef2ff";
                                  e.currentTarget.style.borderColor = "#93c5fd";
                                }
                              }}
                              onMouseLeave={(e) => {
                                e.currentTarget.style.background = "#f8fafc";
                                e.currentTarget.style.borderColor = "#cbd5e1";
                              }}
                            >
                              {updatingId === item.id
                                ? "…"
                                : !item.templateDownloadUrl
                                  ? "N/A"
                                  : item.isEnabled
                                    ? "Disable"
                                    : "Enable"}
                            </button>
                          </div>
                        </div>
                      ))
                    )}
                  </div>

                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <style>{`
        @keyframes perm-spin { to { transform: rotate(360deg); } }
      `}</style>
    </div>
  );
};

export default Permission;
